import { useCallback, useEffect, useRef, useState } from 'react'
import type { AppError, BreathingSession } from '@/types'
import { repositories } from '@/services/repositories'
import { isAppError } from '@/lib/supabase/errors'
import { Button, FeatureCard } from '@/components/ui'
import { PageHeader } from '@/components/navigation'
import { BackIcon, BreathIcon, WarningIcon } from '@/components/icons'
import { BREATHING_EXERCISES, SAFETY_CUE, getExerciseById } from './exercises'
import type { BreathingExercise } from './exercises'
import { useBreathingSettings } from './useBreathingSettings'
import { useReducedMotion } from './useReducedMotion'
import { createSoundCues, type SoundCues } from './soundCues'
import { createHapticCues, type HapticCues } from './haptics'
import { ExerciseDetail } from './components/ExerciseDetail'
import { SessionView } from './components/SessionView'
import { CompletionView, type SessionSaveOutcome } from './components/CompletionView'
import { SessionHistory } from './components/SessionHistory'
import './breathing.css'

type Stage = 'overview' | 'detail' | 'session' | 'done'

type SessionResult = {
  kind: 'completed' | 'interrupted'
  elapsedSeconds: number
}

const UNKNOWN_SAVE_ERROR: AppError = {
  code: 'BREATHING_SAVE_FAILED',
  userMessage: 'Er ging iets mis bij het opslaan. Probeer het later opnieuw.',
  retryable: true,
  source: 'database',
}

const HISTORY_ERROR_FALLBACK: AppError = {
  code: 'BREATHING_HISTORY_FAILED',
  userMessage: 'Je eerdere oefeningen kunnen nu niet worden geladen.',
  retryable: true,
  source: 'database',
}

/**
 * Adem — overview → explanation → duration (1–5 min) → 'Start oefening' →
 * 3-2-1 countdown → active session → pause/stop → completion.
 * Results are saved only after a confirmed end; interrupted sessions are
 * stored as interrupted, never falsely marked complete.
 */
export default function BreathingPage() {
  const settings = useBreathingSettings()
  const reducedMotion = useReducedMotion(settings.reducedMotionSetting)

  const [stage, setStage] = useState<Stage>('overview')
  const [exerciseId, setExerciseId] = useState<string | null>(null)
  const [durationMinutes, setDurationMinutes] = useState(1)
  const [result, setResult] = useState<SessionResult | null>(null)
  const [saveOutcome, setSaveOutcome] = useState<SessionSaveOutcome>('pending')
  const [saveError, setSaveError] = useState<AppError | null>(null)

  const [sessions, setSessions] = useState<BreathingSession[] | null>(null)
  const [historyLoading, setHistoryLoading] = useState(true)
  const [historyError, setHistoryError] = useState<AppError | null>(null)

  const soundCuesRef = useRef<SoundCues | null>(null)
  const hapticCuesRef = useRef<HapticCues | null>(null)

  const exercise: BreathingExercise | undefined = exerciseId
    ? getExerciseById(exerciseId)
    : undefined

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    setHistoryError(null)
    try {
      const list = await repositories.breathing.listSessions(10)
      setSessions(list)
    } catch (error) {
      setSessions(null)
      setHistoryError(isAppError(error) ? error : HISTORY_ERROR_FALLBACK)
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  // Dispose the WebAudio context when the page unmounts.
  useEffect(
    () => () => {
      soundCuesRef.current?.dispose()
      soundCuesRef.current = null
    },
    [],
  )

  const openExercise = useCallback((id: string) => {
    setExerciseId(id)
    setResult(null)
    setStage('detail')
  }, [])

  const backToOverview = useCallback(() => {
    setStage('overview')
  }, [])

  /** Start tap: cues unlock here, inside the user gesture (no autoplay). */
  const handleStart = useCallback(() => {
    soundCuesRef.current?.dispose()
    const sounds = createSoundCues(settings.soundsEnabled)
    sounds.unlock()
    soundCuesRef.current = sounds
    hapticCuesRef.current = createHapticCues(settings.hapticsEnabled)
    setResult(null)
    setSaveOutcome('pending')
    setSaveError(null)
    setStage('session')
  }, [settings.soundsEnabled, settings.hapticsEnabled])

  const finalize = useCallback(
    async (kind: 'completed' | 'interrupted', doneExercise: BreathingExercise, elapsedSeconds: number) => {
      // Cues stay alive so the completion tone can finish; they are disposed
      // on the next Start tap or on page unmount.
      setResult({ kind, elapsedSeconds })
      setStage('done')
      setSaveOutcome('pending')
      setSaveError(null)

      const finishedAt = new Date()
      const startedAt = new Date(finishedAt.getTime() - elapsedSeconds * 1000)
      try {
        await repositories.breathing.saveSession({
          exerciseId: doneExercise.id,
          durationSeconds: elapsedSeconds,
          completed: kind === 'completed',
          interrupted: kind === 'interrupted',
          startedAt: startedAt.toISOString(),
          finishedAt: finishedAt.toISOString(),
        })
        setSaveOutcome('saved')
        void loadHistory()
      } catch (error) {
        if (isAppError(error) && error.code === 'SUPABASE_NOT_CONFIGURED') {
          setSaveOutcome('not_configured')
        } else {
          setSaveOutcome('failed')
          setSaveError(isAppError(error) ? error : UNKNOWN_SAVE_ERROR)
        }
      }
    },
    [loadHistory],
  )

  const handleComplete = useCallback(
    (elapsedSeconds: number) => {
      if (exercise) void finalize('completed', exercise, elapsedSeconds)
    },
    [exercise, finalize],
  )

  const handleInterrupt = useCallback(
    (elapsedSeconds: number) => {
      if (exercise) void finalize('interrupted', exercise, elapsedSeconds)
    },
    [exercise, finalize],
  )

  const handleCancelCountdown = useCallback(() => {
    soundCuesRef.current?.dispose()
    soundCuesRef.current = null
    hapticCuesRef.current = null
    setStage('detail')
  }, [])

  const headerSubtitle =
    stage === 'overview' ? 'Rustige ademhalingsoefeningen' : exercise?.name

  return (
    <div className="breathing-page">
      <PageHeader title="Adem" subtitle={headerSubtitle} />

      {stage === 'detail' && (
        <div className="breathing-page__back">
          <Button variant="ghost" onClick={backToOverview}>
            <span className="breathing-action">
              <BackIcon size={20} />
              Terug naar overzicht
            </span>
          </Button>
        </div>
      )}

      {stage === 'overview' && (
        <>
          <p className="breathing-safety" role="note">
            <WarningIcon size={18} />
            {SAFETY_CUE}
          </p>

          <div className="breathing-overview">
            {BREATHING_EXERCISES.map((item) => (
              <FeatureCard
                key={item.id}
                icon={<BreathIcon size={24} />}
                title={item.name}
                description={item.tagline}
                onClick={() => openExercise(item.id)}
              />
            ))}
          </div>

          <SessionHistory
            sessions={sessions}
            loading={historyLoading}
            error={historyError}
            onRetry={() => void loadHistory()}
          />
        </>
      )}

      {stage === 'detail' && exercise && (
        <ExerciseDetail
          exercise={exercise}
          durationMinutes={durationMinutes}
          onDurationChange={setDurationMinutes}
          onStart={handleStart}
        />
      )}

      {stage === 'session' && exercise && (
        <SessionView
          exercise={exercise}
          durationMinutes={durationMinutes}
          reducedMotion={reducedMotion}
          sounds={soundCuesRef.current ?? createSoundCues(false)}
          haptics={hapticCuesRef.current ?? createHapticCues(false)}
          onComplete={handleComplete}
          onInterrupt={handleInterrupt}
          onCancelCountdown={handleCancelCountdown}
        />
      )}

      {stage === 'done' && exercise && result && (
        <CompletionView
          kind={result.kind}
          exercise={exercise}
          elapsedSeconds={result.elapsedSeconds}
          saveOutcome={saveOutcome}
          saveError={saveError}
          onAgain={() => setStage('detail')}
          onOverview={backToOverview}
        />
      )}
    </div>
  )
}
